/**
 * Detects genuine INCREASES in the server's ticket counts so the UI can
 * animate them and chime once.
 *
 * Counts are never derived or incremented client-side: `observeCounts` is fed
 * the exact numbers the API returned (from the `GET /tickets/counts` query
 * function) and only compares them with the previous server value seen in this
 * page session. It reports an increase only when
 *   - a previous value exists (the first load, and a page refresh, never fire),
 *   - the new value is strictly greater (decreases and no-change never fire),
 *   - the scope is unchanged (a new scope — another user, logout — starts over).
 * Several counters rising in the same response (one new ticket raises both
 * "new" and "unassigned") produce ONE chime.
 */
import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { chimeOnce } from './notificationSound';

export type CountName = 'new' | 'unassigned' | 'assignedToMe';

export interface CountPulse {
  /** Increments on every genuine increase of this counter. */
  token: number;
  from: number;
  to: number;
}

let currentScope: string | null = null;
const lastSeen = new Map<CountName, number>();
let pulses = new Map<CountName, CountPulse>();
const listeners = new Set<() => void>();

function notify(): void {
  listeners.forEach((l) => l());
}

/** Forget everything (logout / user switch) so the next response is a first load. */
export function resetLiveCounts(): void {
  currentScope = null;
  lastSeen.clear();
  pulses = new Map();
  notify();
}

/**
 * Feed one server response. Returns the names that genuinely increased.
 * `scope` identifies what the numbers are about (e.g. the query key); a
 * different scope than last time resets the baseline instead of comparing.
 */
export function observeCounts(scope: string, values: Partial<Record<CountName, number>>): CountName[] {
  if (scope !== currentScope) {
    currentScope = scope;
    lastSeen.clear();
    pulses = new Map();
  }
  const increased: CountName[] = [];
  const next = new Map(pulses);
  for (const name of Object.keys(values) as CountName[]) {
    const value = values[name];
    if (typeof value !== 'number' || !Number.isFinite(value)) continue;
    const prev = lastSeen.get(name);
    lastSeen.set(name, value);
    if (prev !== undefined && value > prev) {
      increased.push(name);
      next.set(name, { token: (pulses.get(name)?.token ?? 0) + 1, from: prev, to: value });
    }
  }
  if (increased.length > 0) {
    pulses = next;
    notify();
    chimeOnce(`${scope}:${increased.map((n) => `${n}=${values[n]}`).join('&')}`);
  }
  return increased;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function useCountPulse(name: CountName | undefined): CountPulse | undefined {
  return useSyncExternalStore(
    subscribe,
    () => (name ? pulses.get(name) : undefined),
    () => undefined,
  );
}

/** How long a genuine increase stays "hot" (card halo + counter bump): 800–1400 ms band, brief and never looping. */
export const PULSE_MS = 1100;

/**
 * True for ~1.1 s after a genuine increase of `name`. A pulse that already
 * existed when the component mounted is ignored (it is not news any more).
 */
export function usePulseActive(name: CountName | undefined): boolean {
  const pulse = useCountPulse(name);
  const initial = useRef(pulse?.token ?? 0);
  const [active, setActive] = useState(false);
  const token = pulse?.token ?? 0;
  useEffect(() => {
    if (token === initial.current) return undefined;
    setActive(true);
    const t = setTimeout(() => setActive(false), PULSE_MS);
    return () => clearTimeout(t);
  }, [token]);
  return active;
}
